import { DataType, EventType, IndexType } from '@doss/prisma/types'
import * as yup from 'yup'
import { ChartSubType } from '../prisma-duplicate-types.js'
import { ActionTriggerType } from './action-trigger.js'
import { ACTION_STEP_NOTIFICATION_TYPES } from './notification.js'
import { ALLOWED_ARRAY_INNER_TYPES } from './table-column.js'

/**
 * The following event types are shown as push notifications to the user whenever they occur. This
 * is done using a pushNotification pubsub topic that is shown as a popup notification for the user.
 */
export const PUSH_NOTIFICATION_EVENTS: EventType[] = [
  EventType.ACTION_START,
  EventType.ACTION_END,
  EventType.ACTION_STEP_NOTIFICATION,
]

export const tableCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableCreate']).required(),
    tableId: yup.string().required(),
    name: yup.string().required(),
  })
  .strict()

export type TableCreateEventData = yup.InferType<typeof tableCreateEventDataSchema>

export const tableDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableDelete']).required(),
    tableId: yup.string().required(),
    name: yup.string().required(),
  })
  .strict()

export type TableDeleteEventData = yup.InferType<typeof tableDeleteEventDataSchema>

export const tableRowCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableRowCreate']).required(),
    tableRowId: yup.string().required(),
    // This is optional to allow for backwards compatibility
    tableRowName: yup.string().optional(),
  })
  .strict()

export type TableRowCreateEventData = yup.InferType<typeof tableRowCreateEventDataSchema>

export const tableRowUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableRowUpdate']).required(),
    tableRowId: yup.string().required(),
    changedValue: yup.string().oneOf(['Order']).required(),
    oldValue: yup.string().required(),
    newValue: yup.string().required(),
  })
  .strict()

export type TableRowUpdateEventData = yup.InferType<typeof tableRowUpdateEventDataSchema>

export const tableRowDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableRowDelete']).required(),
    tableRowId: yup.string().required(),
    rowName: yup.string().required(),
  })
  .strict()

export type TableRowDeleteEventData = yup.InferType<typeof tableRowDeleteEventDataSchema>

export const tableColumnCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableColumnCreate']).required(),
    tableColumnId: yup.string().required(),
    tableColumnName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
  })
  .strict()

export type TableColumnCreateEventData = yup.InferType<typeof tableColumnCreateEventDataSchema>

export const tableColumnUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableColumnUpdate']).required(),
    tableColumnId: yup.string().required(),
    tableColumnName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
    oldValue: yup
      .object({
        name: yup.string().optional(),
        type: yup.string().oneOf(Object.values(DataType)).optional(),
        innerType: yup
          .string()
          .oneOf(Object.values(ALLOWED_ARRAY_INNER_TYPES))
          .optional()
          .nullable(),
        indexType: yup.string().oneOf(Object.values(IndexType)).optional(),
      })
      .required(),
    newValue: yup
      .object({
        name: yup.string().optional(),
        type: yup.string().oneOf(Object.values(DataType)).optional(),
        innerType: yup
          .string()
          .oneOf(Object.values(ALLOWED_ARRAY_INNER_TYPES))
          .optional()
          .nullable(),
        indexType: yup.string().oneOf(Object.values(IndexType)).optional(),
      })
      .required(),
  })
  .strict()

export type TableColumnUpdateEventData = yup.InferType<typeof tableColumnUpdateEventDataSchema>

export const tableColumnDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableColumnDelete']).required(),
    tableColumnId: yup.string().required(),
    tableColumnName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
  })
  .strict()

export type TableColumnDeleteEventData = yup.InferType<typeof tableColumnDeleteEventDataSchema>

export const tableTabCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableTabCreate']).required(),
    tabId: yup.string().required(),
    tabName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
  })
  .strict()

export type TableTabCreateEventData = yup.InferType<typeof tableTabCreateEventDataSchema>

export const tableTabUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableTabUpdate']).required(),
    tabId: yup.string().required(),
    tabName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
    oldValue: yup
      .object({
        name: yup.string().optional(),
      })
      .required(),
    newValue: yup
      .object({
        name: yup.string().optional(),
      })
      .required(),
  })
  .strict()

export type TableTabUpdateEventData = yup.InferType<typeof tableTabUpdateEventDataSchema>

export const tableTabDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableTabDelete']).required(),
    tabId: yup.string().required(),
    tabName: yup.string().required(),
    tableId: yup.string().required(),
    tableName: yup.string().required(),
  })
  .strict()

export type TableTabDeleteEventData = yup.InferType<typeof tableTabDeleteEventDataSchema>

export const tableCellUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['TableCellUpdate']).required(),
    tableCellId: yup.string().required(),
    changedColumnId: yup.string().required(),
    changedColumnName: yup.string().optional(),
    oldValue: yup.string().nullable().defined(),
    newValue: yup.string().nullable().defined(),
  })
  .strict()

export type TableCellUpdateEventData = yup.InferType<typeof tableCellUpdateEventDataSchema>

export const documentCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['DocumentCreate']).required(),
    documentId: yup.string().required(),
    name: yup.string().required(),
    isSignature: yup.boolean().notRequired(),
  })
  .strict()

export type DocumentCreateEventData = yup.InferType<typeof documentCreateEventDataSchema>

export const documentUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['DocumentUpdate']).required(),
    documentId: yup.string().required(),
  })
  .strict()

export type DocumentUpdateEventData = yup.InferType<typeof documentUpdateEventDataSchema>

export const documentDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['DocumentDelete']).required(),
    documentId: yup.string().required(),
    name: yup.string().required(),
  })
  .strict()

export type DocumentDeleteEventData = yup.InferType<typeof documentDeleteEventDataSchema>

export const actionStartEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ActionStart']).required(),
    triggerType: yup.string().oneOf(Object.values(ActionTriggerType)).required(),
    actionId: yup.string().required(),
    actionName: yup.string().required(),
  })
  .strict()

export type ActionStartEventData = yup.InferType<typeof actionStartEventDataSchema>

export const actionEndEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ActionEnd']).required(),
    status: yup.string().oneOf(['success', 'failure']).required(),
    actionId: yup.string().required(),
    actionName: yup.string().required(),
    // Stored events can outlive the frontend that wrote them. Unknown values must fall back to the
    // generic failure UI instead of invalidating the entire activity-feed row.
    errorType: yup.string().optional(),
    errorReason: yup.string().optional(),
  })
  .strict()

export type ActionEndEventData = yup.InferType<typeof actionEndEventDataSchema>

export const actionStepNotificationEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ActionStepNotification']).required(),
    actionStepId: yup.string().required(),
    actionStepName: yup.string().required(),
    metadata: yup
      .object({
        title: yup.string().required(),
        message: yup.string().required(),
        notificationType: yup
          .string()
          .oneOf(Object.values(ACTION_STEP_NOTIFICATION_TYPES))
          .required(),
        linkToPath: yup.string().optional(),
        linkToType: yup.string().required(),
      })
      .required(),
  })
  .strict()

export type ActionStepNotificationEventData = yup.InferType<
  typeof actionStepNotificationEventDataSchema
>

export const chartCreateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ChartCreate']).required(),
    chartId: yup.string().required(),
    chartName: yup.string().required(),
    chartSubType: yup.string().oneOf(Object.values(ChartSubType)).required(),
  })
  .strict()

export enum ChartUpdateType {
  CHART_AGGREGATION_OPERATION = 'CHART_AGGREGATION_OPERATION',
  CHART_FILTERS = 'CHART_FILTERS',
  CHART_GROUP_BYS = 'CHART_GROUP_BYS',
  CHART_NAME = 'CHART_NAME',
  CHART_TYPE = 'CHART_TYPE',
  CHART_X_AXIS = 'CHART_X_AXIS',
  CHART_Y_AXIS = 'CHART_Y_AXIS',
}

const chartUpdateMetadataSchema = yup
  .object({
    type: yup.string().required().oneOf(Object.values(ChartUpdateType)),
    oldValue: yup.string(),
    newValue: yup.string(),
  })
  .required()

export type ChartUpdateMetadata = yup.InferType<typeof chartUpdateMetadataSchema>

export type ChartCreateEventData = yup.InferType<typeof chartCreateEventDataSchema>

export const chartUpdateEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ChartUpdate']).required(),
    chartId: yup.string().required(),
    chartName: yup.string().required(),
    chartSubType: yup.string().oneOf(Object.values(ChartSubType)).required(),
    chartUpdates: yup.array().of(chartUpdateMetadataSchema),
  })
  .strict()

export type ChartUpdateEventData = yup.InferType<typeof chartUpdateEventDataSchema>

export const chartDeleteEventDataSchema = yup
  .object({
    type: yup.string().oneOf(['ChartDelete']).required(),
    chartId: yup.string().required(),
    chartName: yup.string().required(),
    chartSubType: yup.string().oneOf(Object.values(ChartSubType)).required(),
  })
  .strict()

export type ChartDeleteEventData = yup.InferType<typeof chartDeleteEventDataSchema>

export type EventData =
  | TableCreateEventData
  | TableDeleteEventData
  | TableRowCreateEventData
  | TableRowUpdateEventData
  | TableRowDeleteEventData
  | TableColumnCreateEventData
  | TableColumnUpdateEventData
  | TableColumnDeleteEventData
  | TableTabCreateEventData
  | TableTabUpdateEventData
  | TableTabDeleteEventData
  | TableCellUpdateEventData
  | DocumentCreateEventData
  | DocumentUpdateEventData
  | DocumentDeleteEventData
  | ActionStartEventData
  | ActionEndEventData
  | ActionStepNotificationEventData
  | ChartCreateEventData
  | ChartUpdateEventData
  | ChartDeleteEventData
